<script setup>
import { ref } from 'vue'

const videoRef = ref(null)
const isPlaying = ref(false)
const isDismissed = ref(false)

const handleCtaClick = () => {
  if (isDismissed.value) return

  // 1. Trigger CSS slide-down animation
  isDismissed.value = true

  // 2. Play video after smooth 450ms slide out
  setTimeout(() => {
    startVideo()
  }, 450)
}

const startVideo = async () => {
  if (!videoRef.value || isPlaying.value) return

  try {
    isPlaying.value = true
    videoRef.value.currentTime = 2.5 
    await videoRef.value.play()
  } catch (err) {
    console.error("Playback failed:", err)
  }
}
</script>

<template>
  <div id="cover">
    <!-- Subtle Animated Postal Background Elements -->
    <div class="postal-bg-elements" aria-hidden="true">
      <div class="airmail-border top"></div>
      <div class="airmail-border bottom"></div>
      <span class="floating-icon icon-1">✉️</span>
      <span class="floating-icon icon-2">📩</span>
      <span class="floating-icon icon-3">💌</span>
      <span class="floating-icon icon-4">📮</span>
      <span class="floating-icon icon-5">📬</span>
    </div>

    <div class="grid-x cell small-12 text-center content-wrapper">
      <!-- Postbox Video Container -->
      <div class="cell small-12">
        <div class="postbox-container" @click="handleCtaClick">
          <video 
            ref="videoRef"
            preload="auto"
            playsinline 
            muted
            class="postbox-video"
          >
            <!-- Mobile portrait video (applied when screen width is 640px or less) -->
            <source src="/video/snail-mail-mobile.mp4" media="(max-width: 640px)" type="video/mp4" />
            
            <!-- Default / Desktop landscape video -->
            <source src="/video/snail-mail.mp4" type="video/mp4" />
          </video>

          <!-- Red CTA Banner -->
          <div 
            v-if="!isPlaying" 
            id="click-here-cta" 
            class="rubik-m500 overlay-cta"
            :class="{ 'cta-hidden': isDismissed }"
          >
            <span class="key-icon">🔑</span>
            <span class="cta-text">CLICK TO UNLOCK</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Rubik:wght@500;700;800&family=Yusei+Magic&display=swap');

.bm-burger-button {
  display: none;
}

#cover {
  /* Slightly brightened center (#fafafa) to ensure seamless blend with postbox background */
  background: radial-gradient(circle at center, #fafafa 0%, #e5e7eb 60%, #cbd5e1 100%);
  width: 100vw;
  height: 100vh;
  z-index: 10;
  top: 0;
  left: 0;
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

.content-wrapper {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.rubik-m500 {
  font-family: "Rubik", sans-serif;
  font-weight: 800;
  font-style: normal;
}

/* Airmail Border Strips */
.airmail-border {
  position: absolute;
  left: 0;
  width: 100%;
  height: 12px;
  background: repeating-linear-gradient(
    -45deg,
    #d92121 0,
    #d92121 15px,
    #ffffff 15px,
    #ffffff 25px,
    #0055a5 25px,
    #0055a5 40px,
    #ffffff 40px,
    #ffffff 50px
  );
  opacity: 0.45;
  z-index: 2;

  &.top { top: 0; }
  &.bottom { bottom: 0; }
}

/* Floating Background Icons */
.postal-bg-elements {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.floating-icon {
  position: absolute;
  font-size: clamp(1.8rem, 4vw, 3rem);
  opacity: 0.18;
  filter: blur(1px) grayscale(20%);
  animation: float-around 14s infinite ease-in-out alternate;

  &.icon-1 { top: 12%; left: 10%; animation-delay: 0s; }
  &.icon-2 { top: 20%; right: 12%; animation-delay: -3s; }
  &.icon-3 { bottom: 15%; left: 14%; animation-delay: -6s; }
  &.icon-4 { bottom: 22%; right: 10%; animation-delay: -9s; }
  &.icon-5 { top: 50%; left: 5%; animation-delay: -11s; }
}

/* Video Container & Masking */
.postbox-container {
  position: relative;
  display: inline-block;
  cursor: pointer;

  .postbox-video {
    max-width: 50vw;
    max-height: 50vh;
    width: 100%;
    display: block;
    outline: none;
    border: none;

    /* Darken handles white background multiplication cleaner across viewports */
    mix-blend-mode: multiply;

    /* Tighter radial mask to prevent red halo artifacts on portrait mobile edges */
    mask-image: radial-gradient(
      ellipse 75% 70% at 50% 50%,
      black 35%,
      rgba(0, 0, 0, 0.85) 65%,
      transparent 92%
    );
    -webkit-mask-image: radial-gradient(
      ellipse 75% 70% at 50% 50%,
      black 35%,
      rgba(0, 0, 0, 0.85) 65%,
      transparent 92%
    );
  }

  /* Mobile Layout Fixes */
  @media (max-width: 640px) {
    width: 100vw;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .postbox-video {
      /* Hero sizing on portrait screens */
      max-width: 95vw !important;
      max-height: 72vh !important;
      object-fit: contain;

      /* Softens vertical edges specifically for portrait video framing */
      mask-image: radial-gradient(
        ellipse 80% 80% at 50% 48%,
        black 45%,
        rgba(0, 0, 0, 0.8) 75%,
        transparent 95%
      );
      -webkit-mask-image: radial-gradient(
        ellipse 80% 80% at 50% 48%,
        black 45%,
        rgba(0, 0, 0, 0.8) 75%,
        transparent 95%
      );
    }
  }
}

/* Red Royal Mail CTA Banner Overlay */
.overlay-cta {
  position: absolute;
  bottom: 5%;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  border-radius: 12px;
  box-sizing: border-box;
  z-index: 5;
  pointer-events: none;

  display: flex !important;
  align-items: center;
  justify-content: center;
  flex-direction: column !important;
  text-align: center;
  gap: 0.15rem;

  background: linear-gradient(145deg, #e60000, #b30000) !important;
  color: #ffffff !important;
  border-top: 2px solid #ff8080;

  padding: 0.55em 0.8em;
  font-size: clamp(0.7rem, 2vw, 0.95rem);
  font-weight: 800;
  letter-spacing: 0.5px;

  box-shadow: 
    0 8px 20px rgba(0, 0, 0, 0.25),
    inset 0 1px 2px rgba(255, 255, 255, 0.4);

  transition: 
    transform 0.55s cubic-bezier(0.68, -0.4, 0.265, 1.55),
    opacity 0.45s ease-in,
    filter 0.3s ease;

  animation: red-pulse-bottom 2s infinite ease-in-out;

  .key-icon {
    font-size: 1.3em;
    line-height: 1;
    display: block;
    animation: key-jiggle 1.5s infinite alternate;
  }

  .cta-text {
    line-height: 1.1;
    display: block;
    color: #ffffff !important;
  }

  /* Dynamic Slide & Drop Exit */
  &.cta-hidden {
    animation: none;
    transform: translate(-50%, 130%) scale(0.85) rotate(-3deg);
    opacity: 0;
    filter: brightness(0.7);
  }

  @media (max-width: 640px) {
    bottom: 4%;
    width: 75%;
    max-width: 260px;
    padding: 0.65em 0.9em;
    font-size: 0.85rem;
  }
}

/* Keyframe Animations */
@keyframes red-pulse-bottom {
  0%, 100% {
    filter: brightness(1);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  }
  50% {
    filter: brightness(1.15);
    box-shadow: 0 10px 24px rgba(230, 0, 0, 0.45);
  }
}

@keyframes key-jiggle {
  0% { transform: rotate(0deg); }
  50% { transform: rotate(-10deg); }
  100% { transform: rotate(10deg); }
}

@keyframes float-around {
  0% { transform: translateY(0) rotate(0deg) scale(1); }
  50% { transform: translateY(-20px) rotate(6deg) scale(1.05); }
  100% { transform: translateY(15px) rotate(-6deg) scale(0.95); }
}
</style>